/**
 * พิมพ์เขียวการทำเงินเดือน (Payroll Blueprint) — หน้าอ่านอย่างเดียวที่สรุป
 * "บริษัทนี้ทำเงินเดือนยังไง" ทั้งระบบในที่เดียว. ให้ HR ที่เพิ่งเข้ามาเห็นภาพรวม
 * ครบโดยไม่ต้องถามกลับไปกลับมา; และเป็นขั้นตรวจทาน (Review) ของ Setup Wizard.
 */
import { Link } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import {
  Building2, CalendarClock, Clock, Scale, Users, PlusCircle, MinusCircle,
  Moon, CalendarHeart, GitBranch, CalendarOff, Percent, Receipt, CheckCircle2, AlertCircle, Download, Pencil,
} from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { ComplianceAlertList, type ComplianceItem } from '../../components/ui/ComplianceAlert';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/toast';
import { apiDownload } from '../../lib/api-client';
import { useBlueprint, type Blueprint } from '../../hooks';

const baht = (n: number | null | undefined) =>
  n == null ? '—' : `฿${Number(n).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const DOW = ['', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.', 'อา.'];

function Section({ icon, title, hint, editTo, children }: { icon: ReactNode; title: string; hint?: string; editTo?: string; children: ReactNode }) {
  return (
    <section className="card p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-teal">{icon}</span>
        <h2 className="font-semibold text-gray-900">{title}</h2>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {hint && <span className="text-xs text-gray-400">{hint}</span>}
          {editTo && (
            <Link to={editTo} className="text-xs text-teal hover:underline inline-flex items-center gap-1">
              <Pencil className="w-3.5 h-3.5" /> แก้ไข
            </Link>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm border-b border-gray-50 last:border-0">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-900 text-right font-medium">{children}</span>
    </div>
  );
}

function Flag({ on, children }: { on: boolean; children: ReactNode }) {
  return (
    <span className={`badge ${on ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-400'} mr-1`}>{children}</span>
  );
}

export function OwnerBlueprint() {
  const { data: bp, isLoading, isError, refetch } = useBlueprint();
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);

  async function downloadPdf() {
    setDownloading(true);
    try {
      await apiDownload('/owner/blueprint/pdf', 'payroll-blueprint.pdf');
      toast.success('ดาวน์โหลดพิมพ์เขียว (PDF) แล้ว — ส่งให้ HR ที่เพิ่งเข้ามาได้เลย');
    } catch {
      toast.error('สร้าง PDF ไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <AdminLayout title="พิมพ์เขียวการทำเงินเดือน">
      <div className="max-w-2xl mx-auto space-y-3 pb-10">
        <p className="text-sm text-gray-500 px-1">
          สรุปวิธีทำเงินเดือนของบริษัทนี้ทั้งหมดในหน้าเดียว — รอบจ่าย ค่าจ้าง เงินเพิ่ม เงินหัก กะ วันหยุด สิทธิ์ลา และผู้อนุมัติตัวจริง
          เพื่อให้ทุกคน (รวมถึง HR ที่เพิ่งเข้ามา) เข้าใจตรงกันโดยไม่ต้องถามซ้ำ
        </p>

        {bp && (
          <button type="button" onClick={downloadPdf} disabled={downloading} className="btn-secondary w-full justify-center flex items-center gap-2">
            <Download className="w-4 h-4" /> {downloading ? 'กำลังสร้าง PDF…' : 'ดาวน์โหลด PDF (ส่งให้ HR ใหม่)'}
          </button>
        )}

        {isLoading && <div className="card p-6 text-center text-gray-400">กำลังโหลด…</div>}
        {isError && (
          <EmptyState
            icon={AlertCircle}
            title="โหลดพิมพ์เขียวไม่สำเร็จ"
            detail="ลองใหม่อีกครั้ง"
            action={{ label: 'ลองใหม่', onClick: () => refetch() }}
          />
        )}

        {bp && <BlueprintBody bp={bp} />}
      </div>
    </AdminLayout>
  );
}

function BlueprintBody({ bp }: { bp: Blueprint }) {
  // === readiness / what's missing (top of page) ===
  const alerts: ComplianceItem[] = [];
  for (const it of bp.readiness.items.filter((i) => !i.done && !i.optional)) {
    alerts.push({ level: 'warn', title: `ยังไม่ได้ตั้ง: ${it.label}`, action: { label: 'ไปตั้งค่า', to: it.route } });
  }
  if (bp.workforce.salary_missing.length > 0) {
    alerts.push({
      level: 'block',
      title: 'มีพนักงานยังไม่ได้ตั้งฐานเงินเดือน',
      detail: bp.workforce.salary_missing.map((e) => e.name).join(', '),
      count: bp.workforce.salary_missing.length,
      cite: 'ปิดงวดไม่ได้จนกว่าจะตั้งครบ',
      action: { label: 'ตั้งฐานเงินเดือน', to: '/hr/employees' },
    });
  }

  const s = bp.statutory;

  return (
    <>
      {/* readiness banner */}
      <div className={`card p-4 ${bp.readiness.complete ? 'bg-teal-50/40' : ''}`}>
        <div className="flex items-center gap-2">
          {bp.readiness.complete
            ? <CheckCircle2 className="w-5 h-5 text-teal" />
            : <AlertCircle className="w-5 h-5 text-amber-500" />}
          <span className="font-semibold text-gray-900">
            {bp.readiness.complete ? 'ตั้งค่าครบพร้อมทำเงินเดือน' : `ตั้งค่าแล้ว ${bp.readiness.pct}%`}
          </span>
        </div>
        {alerts.length > 0 && <div className="mt-3"><ComplianceAlertList items={alerts} /></div>}
      </div>

      {/* 1. company */}
      <Section icon={<Building2 className="w-5 h-5" />} title="บริษัท" editTo="/owner/settings#company">
        <Row label="ชื่อบริษัท">{bp.company.name || '—'}</Row>
        <Row label="เลขผู้เสียภาษี">{bp.company.tax_id || '—'}</Row>
        <Row label="ผู้ลงนามในสลิป">{bp.company.signer_name ? `${bp.company.signer_name}${bp.company.signer_title ? ` (${bp.company.signer_title})` : ''}` : '—'}</Row>
      </Section>

      {/* 2. pay cycle */}
      <Section icon={<CalendarClock className="w-5 h-5" />} title="รอบจ่ายเงินเดือน" editTo="/owner/settings#payroll">
        <Row label="ตัดรอบวันที่">{bp.pay_cycle.cutoff_day != null ? `ทุกวันที่ ${bp.pay_cycle.cutoff_day}` : '—'}</Row>
        <Row label="จ่ายเงินวันที่">{bp.pay_cycle.payday_day != null ? `ทุกวันที่ ${bp.pay_cycle.payday_day}` : '—'}</Row>
        <Row label="ย้อนหลังข้ามรอบ">{bp.pay_cycle.backdated_mode === 'carry_forward' ? 'ยกไปงวดถัดไป' : bp.pay_cycle.backdated_mode || 'ตามค่ามาตรฐาน'}</Row>
      </Section>

      {/* 3. work rules */}
      <Section icon={<Clock className="w-5 h-5" />} title="กติกาเวลาทำงาน" editTo="/hr/time#settings">
        <Row label="ทำงาน/สัปดาห์">{bp.work_rules.workdays_per_week} วัน</Row>
        <Row label="วันหยุดประจำสัปดาห์">{(bp.work_rules.weekend_days ?? []).map((d) => DOW[d] ?? d).join(' ') || '—'}</Row>
        <Row label="ชั่วโมง/วัน">{bp.work_rules.hours_per_workday} ชม.</Row>
        <Row label="อนุโลมมาสาย">{bp.work_rules.late_grace_minutes} นาที</Row>
        <Row label="เพดาน OT/สัปดาห์">{bp.work_rules.ot_weekly_cap_hours} ชม.</Row>
      </Section>

      {/* 4. statutory */}
      <Section icon={<Scale className="w-5 h-5" />} title={`อัตราตามกฎหมาย ปี ${s.year}`} hint="ระบบคำนวณให้อัตโนมัติ">
        <Row label="ประกันสังคม">{s.sso.rate_percent != null ? `${s.sso.rate_percent}% (เพดานฐาน ${baht(s.sso.ceiling)} → สูงสุด ${baht(s.sso.monthly_cap)}/เดือน)` : '—'}</Row>
        <Row label="ลดหย่อนส่วนตัว">{baht(s.personal_allowance)}</Row>
        <Row label="ค่าใช้จ่าย">{s.expense_allowance_note}</Row>
        <Row label="ค่าแรงขั้นต่ำ">{s.min_wage != null ? `${baht(s.min_wage)}/วัน` : 'ตามประกาศจังหวัด'}</Row>
        <Row label="อัตรา OT">{`ปกติ ×${s.ot_multipliers.ot1} · ทำงานวันหยุด ×${s.ot_multipliers.holiday_work_monthly}(รายเดือน)/×${s.ot_multipliers.holiday_work_daily}(รายวัน) · OT วันหยุด ×${s.ot_multipliers.holiday_ot}`}</Row>
      </Section>

      {/* 5. workforce */}
      <Section icon={<Users className="w-5 h-5" />} title="พนักงาน & ฐานเงินเดือน" editTo="/hr/people#all">
        <Row label="พนักงานที่ทำงานอยู่">{bp.workforce.total_active} คน</Row>
        <Row label="แบ่งตามประเภท">{`รายเดือน ${bp.workforce.monthly} · รายวัน ${bp.workforce.daily}`}</Row>
        <Row label="ตั้งฐานเงินเดือนแล้ว">{`${bp.workforce.salary_covered}/${bp.workforce.total_active} คน`}</Row>
      </Section>

      {/* 6. allowances */}
      <Section icon={<PlusCircle className="w-5 h-5" />} title="เงินเพิ่ม (Allowance)" hint={`${bp.allowances.length} รายการ`} editTo="/owner/wage#allowance">
        {bp.allowances.length === 0 ? <p className="text-sm text-gray-400">— ยังไม่มี —</p> : (
          <div className="space-y-2">
            {bp.allowances.map((a, i) => (
              <div key={i} className="text-sm border-b border-gray-50 last:border-0 pb-2 last:pb-0">
                <div className="flex justify-between"><span className="font-medium text-gray-900">{a.name}</span><span className="text-gray-500">{a.eligible_count} คนได้รับ</span></div>
                <div className="mt-1">
                  <Flag on={a.taxable}>ภาษี</Flag><Flag on={a.ssoable}>ปกส.</Flag><Flag on={a.pfable}>กองทุน</Flag>
                  <span className="text-xs text-gray-400 ml-1">{a.frequency === 'fixed_monthly' ? `คงที่ ${baht(a.default_amount)}/เดือน` : 'ผันแปร'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 7. deductions */}
      <Section icon={<MinusCircle className="w-5 h-5" />} title="เงินหัก (Deduction)" editTo="/owner/wage#deduction">
        <div className="space-y-1">
          {bp.deductions.statutory.map((d, i) => <Row key={`s${i}`} label={d.name}>ตามกฎหมาย</Row>)}
          {bp.deductions.fixed.map((d, i) => <Row key={`f${i}`} label={d.name}>{`${baht(d.default_amount)} · ${d.eligible_count} คน`}</Row>)}
          {bp.deductions.fixed.length === 0 && bp.deductions.statutory.length === 0 && <p className="text-sm text-gray-400">— ยังไม่มี —</p>}
        </div>
        <p className="text-xs text-gray-400 mt-2">{bp.deductions.section76_note}</p>
      </Section>

      {/* 8. shifts */}
      {bp.shifts.length > 0 && (
        <Section icon={<Moon className="w-5 h-5" />} title="กะการทำงาน" hint={`${bp.shifts.length} กะ`} editTo="/hr/time#shifts">
          {bp.shifts.map((sh, i) => (
            <Row key={i} label={sh.name}>{`${sh.start}–${sh.end}${sh.crosses_midnight ? ' (ข้ามคืน)' : ''}${sh.night_premium_type !== 'none' ? ' · มีเบี้ยกะดึก' : ''}`}</Row>
          ))}
        </Section>
      )}

      {/* 9. leave */}
      <Section icon={<CalendarHeart className="w-5 h-5" />} title="สิทธิ์การลา" hint={`มอบแล้ว ${bp.leave.assigned_count} คน`} editTo="/hr/time#leave">
        {bp.leave.packages.length === 0 ? <p className="text-sm text-gray-400">— ยังไม่มีชุดสิทธิ์ —</p> : bp.leave.packages.map((p, i) => (
          <Row key={i} label={p.name}>{`ป่วย ${p.sick ?? 0} · กิจ ${p.personal ?? 0} · พักร้อน ${p.vacation ?? 0} วัน${(p.carry_over_max ?? 0) > 0 ? ` · ยกยอด≤${p.carry_over_max}` : ''}`}</Row>
        ))}
      </Section>

      {/* 10. approval */}
      <Section icon={<GitBranch className="w-5 h-5" />} title="สายอนุมัติ (ผู้อนุมัติตัวจริง)" hint={`${bp.approval.levels} ชั้น`} editTo="/hr/people#all">
        {bp.approval.by_level.map((lv) => (
          <Row key={lv.level} label={`ชั้น ${lv.level}`}>
            {lv.approvers.length ? lv.approvers.map((a) => `${a.name} (${a.role})`).join(', ') : <span className="text-amber-600">ยังไม่กำหนด</span>}
          </Row>
        ))}
        {bp.approval.by_level.length === 0 && <p className="text-sm text-amber-600">ยังไม่ตั้งสายอนุมัติระดับบริษัท — ใช้หัวหน้างานรายคน หรือค่ามาตรฐานแทน</p>}

        <Row label="พนักงานที่มีผู้อนุมัติแล้ว">
          {bp.approval.notify.employees_with_approver} คน
          {bp.approval.notify.employees_using_fallback > 0 && (
            <span className="text-amber-600"> · อีก {bp.approval.notify.employees_using_fallback} คนยังไม่ตั้ง</span>
          )}
        </Row>
        <Row label="ถ้าไม่ได้ตั้งผู้อนุมัติ ใครรับแจ้งเตือน">
          {bp.approval.notify.fallback_role === 'none'
            ? <span className="text-amber-600">ยังไม่มี HR/เจ้าของในระบบ — คำขออาจไม่ถึงใคร</span>
            : <>
                {bp.approval.notify.fallback_role === 'hr' ? 'ฝ่ายบุคคล (HR)' : 'เจ้าของ (Owner)'}
                {bp.approval.notify.fallback_recipients.length > 0 && `: ${bp.approval.notify.fallback_recipients.join(', ')}`}
              </>}
        </Row>
      </Section>

      {/* 11. holidays */}
      <Section icon={<CalendarOff className="w-5 h-5" />} title={`วันหยุดประจำปี ${bp.holidays.year}`} hint={`${bp.holidays.count} วัน`} editTo="/hr/time#holidays">
        {bp.holidays.count === 0 ? <p className="text-sm text-amber-600">ยังไม่ตั้งวันหยุด</p> : (
          <div className="flex flex-wrap gap-1.5">
            {bp.holidays.items.slice(0, 24).map((h, i) => (
              <span key={i} className="badge bg-gray-100 text-gray-600">{h.date.slice(5)} {h.name}</span>
            ))}
          </div>
        )}
      </Section>

      {/* 12. commission */}
      {bp.commission.uses && (
        <Section icon={<Percent className="w-5 h-5" />} title="ค่าคอมมิชชั่น" editTo="/owner/wage#commission">
          {bp.commission.packages.map((p, i) => (
            <Row key={i} label={p.name}>{`ฐาน ${p.basis} · ${p.tiers} ขั้น`}</Row>
          ))}
        </Section>
      )}

      {/* 13. sample slip */}
      {bp.sample_slip && (
        <Section icon={<Receipt className="w-5 h-5" />} title="ตัวอย่างการคำนวณสลิป" hint={`จากงวด ${bp.sample_slip.period}`}>
          <p className="text-xs text-gray-400 mb-2">ตัวอย่างจริงจากงวดล่าสุด (ไม่ระบุชื่อ) — แสดงว่าสลิปประกอบจากอะไรบ้าง</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-green-700 mb-1">รายได้</p>
              {(bp.sample_slip.line_items.earnings ?? []).map((e, i) => (
                <div key={i} className="flex justify-between text-xs py-0.5"><span className="text-gray-600">{e.label}</span><span>{baht(e.amount)}</span></div>
              ))}
            </div>
            <div>
              <p className="text-xs font-medium text-red-600 mb-1">รายการหัก</p>
              {(bp.sample_slip.line_items.deductions ?? []).map((d, i) => (
                <div key={i} className="flex justify-between text-xs py-0.5"><span className="text-gray-600">{d.label}</span><span>{d.amount < 0 ? `+${baht(-d.amount)}` : `-${baht(d.amount)}`}</span></div>
              ))}
            </div>
          </div>
        </Section>
      )}

      <p className="text-center text-xs text-gray-300 pt-2">
        อัปเดต {new Date(bp.generated_at).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })}
      </p>

      <div className="text-center">
        <Link to="/owner/settings" className="text-sm font-medium text-teal hover:underline">ไปแก้ไขการตั้งค่า →</Link>
      </div>
    </>
  );
}
